import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBlogPosts, slugify } from '@/hooks/useBlogStore';
import RichTextEditor from '@/components/feature/RichTextEditor';
import type { BlogPost } from '@/mocks/blog';
import type { LexicalEditor } from 'lexical';

export default function BlogAdminEdit() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { posts, savePost } = useBlogPosts();

  const existingPost = slug ? posts.find((p) => p.slug === slug) : undefined;
  const isNew = !existingPost;

  const [title, setTitle] = useState(existingPost?.title || '');
  const [category, setCategory] = useState(existingPost?.category || '');
  const [excerpt, setExcerpt] = useState(existingPost?.excerpt || '');
  const [readTime, setReadTime] = useState(existingPost?.readTime || '');
  const [publishedAt, setPublishedAt] = useState(existingPost?.publishedAt || new Date().toISOString().split('T')[0]);
  const [author, setAuthor] = useState(existingPost?.author || 'Dental Safety Abroad Team');
  const [tagsInput, setTagsInput] = useState(existingPost?.tags?.join(', ') || '');
  const [imageUrl, setImageUrl] = useState(existingPost?.image || '');
  const [contentHtml, setContentHtml] = useState(existingPost?.content || '');
  const [saved, setSaved] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docError, setDocError] = useState('');
  const [imagePreview, setImagePreview] = useState(existingPost?.image || '');

  const editorRef = useRef<LexicalEditor | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setCategory(existingPost.category);
      setExcerpt(existingPost.excerpt);
      setReadTime(existingPost.readTime);
      setPublishedAt(existingPost.publishedAt);
      setAuthor(existingPost.author);
      setTagsInput(existingPost.tags.join(', '));
      setImageUrl(existingPost.image);
      setImagePreview(existingPost.image);
      setContentHtml(existingPost.content);
    }
  }, [existingPost]);

  const handleSave = useCallback(() => {
    if (!title.trim()) {
      alert('Please enter a post title.');
      return;
    }

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const autoReadTime = readTime.trim()
      ? readTime.trim()
      : `${Math.max(1, Math.ceil(contentHtml.replace(/<[^>]*>/g, '').split(/\s+/).length / 200))} min read`;

    const finalSlug = isNew ? slugify(title) : (slug || slugify(title));

    const post: BlogPost = {
      slug: finalSlug,
      title: title.trim(),
      excerpt: excerpt.trim(),
      content: contentHtml,
      category: category.trim() || 'Uncategorised',
      readTime: autoReadTime,
      publishedAt: publishedAt || new Date().toISOString().split('T')[0],
      author: author.trim() || 'Dental Safety Abroad Team',
      image: imageUrl || imagePreview,
      tags: tags.length > 0 ? tags : ['General'],
    };

    savePost(post);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);

    if (isNew) {
      navigate(`/blog/admin/edit/${finalSlug}`, { replace: true });
    }
  }, [title, category, excerpt, readTime, publishedAt, author, tagsInput, imageUrl, imagePreview, contentHtml, isNew, slug, savePost, navigate]);

  const handlePreview = useCallback(() => {
    // Save first so preview shows updated content
    if (!title.trim()) {
      alert('Please enter a post title first.');
      return;
    }

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const autoReadTime = readTime.trim()
      ? readTime.trim()
      : `${Math.max(1, Math.ceil(contentHtml.replace(/<[^>]*>/g, '').split(/\s+/).length / 200))} min read`;

    const finalSlug = isNew ? slugify(title) : (slug || slugify(title));

    const post: BlogPost = {
      slug: finalSlug,
      title: title.trim(),
      excerpt: excerpt.trim(),
      content: contentHtml,
      category: category.trim() || 'Uncategorised',
      readTime: autoReadTime,
      publishedAt: publishedAt || new Date().toISOString().split('T')[0],
      author: author.trim() || 'Dental Safety Abroad Team',
      image: imageUrl || imagePreview,
      tags: tags.length > 0 ? tags : ['General'],
    };

    savePost(post);

    // Open preview in new tab
    window.open(`/blog/${finalSlug}`, '_blank');
  }, [title, category, excerpt, readTime, publishedAt, author, tagsInput, imageUrl, imagePreview, contentHtml, isNew, slug, savePost]);

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setImagePreview(dataUrl);
      setImageUrl('');
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setImageUrl(url);
    if (url) setImagePreview(url);
  }, []);

  const handleDocUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      setDocError('Please upload a .docx (Word Document) file.');
      return;
    }

    setUploadingDoc(true);
    setDocError('');

    try {
      const mammoth = await import('mammoth');
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setContentHtml(result.value);

      if (result.messages.length > 0) {
        console.warn('Mammoth conversion messages:', result.messages);
      }
    } catch (err) {
      console.error('Failed to parse Word document:', err);
      setDocError('Failed to parse the Word document. Please try a different file.');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  }, []);

  return (
    <main className="bg-background-50 min-h-screen pt-24 md:pt-28">
      <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-sm">
            <Link
              to="/blog/admin"
              className="text-foreground-500 hover:text-primary-600 transition-colors"
            >
              Blog Admin
            </Link>
            <span className="text-foreground-400">/</span>
            <span className="text-foreground-700 font-medium">
              {isNew ? 'New Post' : 'Edit Post'}
            </span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {isNew ? 'Create New Post' : 'Edit Post'}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                {isNew ? 'Write and publish a new blog post.' : `Editing: ${title || 'Untitled'}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handlePreview}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                Preview
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-save-line"></i>
                </span>
                {saved ? 'Saved!' : 'Save Post'}
              </button>
            </div>
          </div>

          {saved && (
            <div className="mb-6 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-green-600">
                <i className="ri-checkbox-circle-fill"></i>
              </span>
              Post saved successfully!
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Post Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter post title..."
                  className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Excerpt
                </label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Brief summary of the post..."
                  rows={3}
                  maxLength={300}
                  className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                />
                <p className="text-xs text-foreground-400 mt-1">{excerpt.length}/300</p>
              </div>

              {/* Word Doc upload */}
              <div className="p-4 bg-background-100 rounded-lg border border-background-200">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Import from Word Document (.docx)
                </label>
                <p className="text-xs text-foreground-500 mb-3">
                  Upload a Word document to populate the content area. Existing content will be replaced.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".docx"
                    onChange={handleDocUpload}
                    className="text-sm text-foreground-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 file:cursor-pointer cursor-pointer"
                  />
                  {uploadingDoc && (
                    <span className="text-sm text-foreground-500 flex items-center gap-1">
                      <span className="w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></span>
                      Parsing...
                    </span>
                  )}
                </div>
                {docError && (
                  <p className="text-xs text-red-600 mt-2">{docError}</p>
                )}
              </div>

              {/* Rich Text Editor */}
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Content
                </label>
                <RichTextEditor
                  key={slug}
                  initialHtml={contentHtml}
                  onChange={setContentHtml}
                  editorRef={editorRef}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Cover Image */}
              <div className="p-4 bg-background-50 border border-background-200 rounded-lg">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Cover Image
                </label>
                {imagePreview ? (
                  <div className="relative mb-3 rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={imagePreview}
                      alt="Cover preview"
                      className="w-full h-40 object-cover object-top"
                    />
                    <button
                      type="button"
                      onClick={() => { setImagePreview(''); setImageUrl(''); }}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors cursor-pointer"
                      title="Remove image"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ) : (
                  <div className="mb-3 h-40 rounded-lg bg-background-100 border-2 border-dashed border-background-300 flex items-center justify-center text-foreground-400">
                    <div className="text-center">
                      <span className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                        <i className="ri-image-add-line text-xl"></i>
                      </span>
                      <span className="text-xs">No cover image</span>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="text-xs text-foreground-700 w-full file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-background-200 file:text-foreground-700 hover:file:bg-background-300 file:cursor-pointer cursor-pointer mb-2"
                />
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-xs text-foreground-500">or paste URL:</span>
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={handleImageUrlChange}
                    placeholder="https://..."
                    className="flex-1 px-2 py-1 text-xs border border-background-200 rounded bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              {/* Metadata */}
              <div className="p-4 bg-background-50 border border-background-200 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Category
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Safety, Destinations"
                    list="categories"
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                  />
                  <datalist id="categories">
                    <option value="Safety" />
                    <option value="Destinations" />
                    <option value="Treatments" />
                    <option value="Practical Guides" />
                    <option value="News" />
                  </datalist>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    placeholder="e.g. Hungary, Implants, Safety"
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Author
                  </label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Read Time
                    </label>
                    <input
                      type="text"
                      value={readTime}
                      onChange={(e) => setReadTime(e.target.value)}
                      placeholder="Auto"
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Published Date
                    </label>
                    <input
                      type="date"
                      value={publishedAt}
                      onChange={(e) => setPublishedAt(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Quick links */}
              <div className="p-4 bg-background-50 border border-background-200 rounded-lg">
                <h3 className="text-sm font-semibold text-foreground-800 mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <Link
                    to="/blog"
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </span>
                    View Blog (public)
                  </Link>
                  <Link
                    to="/blog/admin"
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-list-check"></i>
                    </span>
                    All Posts (admin)
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}